import { useTranslation } from 'react-i18next';
import { BackButton } from '../components/BackButton.js';
import { CallUsNotice } from '../components/CallUsNotice.js';

/**
 * Party size first.
 *
 * It is asked before anything else because availability depends on it: a widget
 * that shows times and then discovers no six-top is free has wasted the guest's
 * attention and will lose them.
 *
 * Buttons rather than a dropdown up to 8, because a dropdown on a phone opens a
 * picker wheel for what should be one tap. Larger parties fall through to a
 * number input, since they are rarer and often need a phone call anyway.
 */
const QUICK_SIZES = [1, 2, 3, 4, 5, 6, 7, 8];

export interface PartyBreakdown {
  kids02: number;
  kids36: number;
  kids712: number;
}

export function PartySizeStep({
  value,
  onChange,
  breakdown,
  onBreakdownChange,
  onNext,
  onBack,
}: {
  value: number;
  onChange: (next: number) => void;
  breakdown: PartyBreakdown;
  onBreakdownChange: (next: PartyBreakdown) => void;
  onNext: () => void;
  /** Only when a branch was chosen here rather than arriving in the URL. */
  onBack?: () => void;
}) {
  const { t } = useTranslation();

  const kids = breakdown.kids02 + breakdown.kids36 + breakdown.kids712;
  const adults = Math.max(0, value - kids);

  /**
   * Changing a child count RAISES the party rather than carving children out
   * of it.
   *
   * A guest who has chosen "4" and then says two of them are toddlers means six
   * people, not two adults and two toddlers — they are answering "who else is
   * coming", not restating the number they already gave. Taking the other
   * reading silently shrinks their booking, which they discover at the door.
   */
  const setKids = (key: keyof PartyBreakdown, next: number) => {
    const clamped = Math.max(0, Math.min(20, next));
    const delta = clamped - breakdown[key];
    onBreakdownChange({ ...breakdown, [key]: clamped });
    if (delta !== 0) onChange(Math.max(1, Math.min(50, value + delta)));
  };

  return (
    <section className="flex flex-col gap-4">
      {onBack ? <BackButton onClick={onBack} label={t('widget.backToVenues')} /> : null}
      <h2 className="text-sm font-medium">{t('common.partySize')}</h2>

      <div className="grid grid-cols-4 gap-2">
        {QUICK_SIZES.map((size) => (
          <button
            key={size}
            type="button"
            data-testid="party-size"
            onClick={() => {
              onChange(size);
              onNext();
            }}
            aria-pressed={value === size}
            className={[
              'rounded-lg border py-3 text-lg font-medium transition-colors',
              value === size
                ? 'border-brand-teal bg-brand-teal text-white'
                : 'border-slate-200 hover:border-brand-teal',
            ].join(' ')}
            data-numeric
          >
            {size}
          </button>
        ))}
      </div>

      {/* Age bands, because a high chair is not a seat and a table of two
          adults with four toddlers is not a table of six. Optional: a party
          with no children never touches these. */}
      <fieldset className="rounded-lg border border-slate-200 p-3">
        <legend className="px-1 text-sm text-slate-600">{t('widget.children')}</legend>
        <div className="grid grid-cols-3 gap-2">
          {([
            ['kids02', 'widget.kids02'],
            ['kids36', 'widget.kids36'],
            ['kids712', 'widget.kids712'],
          ] as const).map(([key, label]) => (
            <label key={key} className="flex flex-col gap-1 text-xs">
              <span className="text-slate-600">{t(label)}</span>
              <input
                type="number"
                min={0}
                max={20}
                value={breakdown[key]}
                onChange={(event) => setKids(key, Number(event.target.value))}
                className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-center font-mono"
                data-numeric
              />
            </label>
          ))}
        </div>
        <p className="mt-2 text-xs text-slate-500" data-numeric>
          {t('widget.partyTotal', { adults, kids, total: value })}
        </p>
      </fieldset>

      <label className="flex items-center gap-2 text-sm">
        <span className="text-slate-600">{t('widget.largerParty')}</span>
        <input
          type="number"
          min={9}
          max={50}
          value={value > 8 ? value : ''}
          onChange={(event) => {
            const next = Number(event.target.value);
            if (next >= 9 && next <= 50) onChange(next);
          }}
          className="w-20 rounded-md border border-slate-200 px-2 py-1.5 font-mono"
        />
      </label>

      {value >= 12 ? <CallUsNotice reason="largeParty" /> : null}

      <button
        type="button"
        onClick={onNext}
        className="rounded-lg bg-brand-teal py-3 font-medium text-white hover:bg-brand-teal-dark"
      >
        {t('widget.next')}
      </button>
    </section>
  );
}
